import { useEffect, useRef, useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { images, portfolioCategories } from "@/config/site";
import { trackEvent } from "@/lib/analytics";

export function Gallery({ items, showFilters = true }) {
  const [filter, setFilter] = useState("All");
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const touchStartX = useRef(null);
  const dialogRef = useRef(null);
  const openerRef = useRef(null);

  const visible = filter === "All" ? items : items.filter((i) => i.category === filter);
  const open = lightboxIndex !== null;
  const current = open ? visible[lightboxIndex] : null;

  const move = (dir) =>
    setLightboxIndex((i) => (i === null ? null : (i + dir + visible.length) % visible.length));

  useEffect(() => {
    if (!open) return undefined;
    document.body.style.overflow = "hidden";
    const dialog = dialogRef.current;
    const getFocusables = () =>
      dialog ? Array.from(dialog.querySelectorAll("button:not([disabled])")) : [];
    getFocusables()[0]?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") setLightboxIndex(null);
      if (e.key === "ArrowRight") move(1);
      if (e.key === "ArrowLeft") move(-1);
      if (e.key === "Tab") {
        const els = getFocusables();
        if (!els.length) return;
        const first = els[0];
        const last = els[els.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKey);
      openerRef.current?.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lightboxIndex]);

  return (
    <div>
      {showFilters && (
        <div className="mb-8 flex flex-wrap gap-2" role="group" aria-label="Filter projects by category">
          {portfolioCategories.map((cat) => (
            <button
              key={cat}
              type="button"
              data-testid={`gallery-filter-${cat.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              aria-pressed={filter === cat}
              onClick={() => setFilter(cat)}
              className={`min-h-[44px] rounded-full border px-5 py-2 text-sm font-semibold transition-[background-color,border-color,color] duration-200 ${
                filter === cat
                  ? "border-ember bg-ember text-white"
                  : "border-slate-300 bg-white text-slate-600 hover:border-ember hover:text-ember-dark"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="gallery-grid">
        {visible.map((item, i) => (
          <button
            key={item.id}
            type="button"
            data-testid={`gallery-item-${item.id}`}
            onClick={(e) => {
              openerRef.current = e.currentTarget;
              setLightboxIndex(i);
              trackEvent("gallery_open", { project: item.id });
            }}
            className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white text-left shadow-sm transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1 hover:border-ember/40 hover:shadow-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-ember"
          >
            <div className="aspect-[4/3] overflow-hidden">
              <img
                src={images[item.image].url}
                alt={images[item.image].alt}
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            </div>
            <div className="p-5">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-ember">{item.category}</p>
              <h3 className="mt-1 font-display text-lg font-semibold text-slate-900">{item.title}</h3>
              {item.concept && (
                <span className="mt-3 inline-block rounded-full border border-amber-500/30 bg-amber-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-amber-700">
                  Concept image
                </span>
              )}
            </div>
          </button>
        ))}
      </div>

      {open && current && (
        <div
          ref={dialogRef}
          className="fixed inset-0 z-[70] flex flex-col bg-slate-950/95 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label={`${current.title} — image viewer`}
          data-testid="gallery-lightbox"
          onTouchStart={(e) => {
            touchStartX.current = e.touches[0].clientX;
          }}
          onTouchEnd={(e) => {
            if (touchStartX.current === null) return;
            const dx = e.changedTouches[0].clientX - touchStartX.current;
            if (Math.abs(dx) > 50) move(dx < 0 ? 1 : -1);
            touchStartX.current = null;
          }}
        >
          <div className="flex items-center justify-between p-4">
            <p className="text-sm font-medium text-slate-300">
              {lightboxIndex + 1} / {visible.length}
            </p>
            <button
              type="button"
              data-testid="lightbox-close"
              aria-label="Close image viewer"
              onClick={() => setLightboxIndex(null)}
              className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-white transition-colors duration-200 hover:bg-white/10"
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
          <div className="relative flex flex-1 items-center justify-center px-4 sm:px-16">
            <button
              type="button"
              data-testid="lightbox-prev"
              aria-label="Previous image"
              onClick={() => move(-1)}
              className="absolute left-3 top-1/2 inline-flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-slate-950/60 text-white transition-colors duration-200 hover:bg-white/10"
            >
              <ChevronLeft className="h-6 w-6" aria-hidden="true" />
            </button>
            <img
              src={images[current.image].url}
              alt={images[current.image].alt}
              className="max-h-[70vh] w-auto max-w-full rounded-xl object-contain shadow-2xl"
              data-testid="lightbox-image"
            />
            <button
              type="button"
              data-testid="lightbox-next"
              aria-label="Next image"
              onClick={() => move(1)}
              className="absolute right-3 top-1/2 inline-flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-slate-950/60 text-white transition-colors duration-200 hover:bg-white/10"
            >
              <ChevronRight className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
          <div className="p-5 text-center">
            <p className="font-display text-lg font-semibold text-white">{current.title}</p>
            <p className="mx-auto mt-1 max-w-xl text-sm text-slate-400">{current.caption}</p>
          </div>
        </div>
      )}
    </div>
  );
}
